import type { ViewKind } from './weekCalendar';

/**
 * Google-Calendar-style deep links for the calendar view.
 *
 * Shape:  /calendar/r/<view>/<year>/<month>/<day>
 * e.g.    /calendar/r/month/2024/7/1
 *
 * `month` and `day` are 1-based (July = 7, first of month = 1), matching
 * Google's `…/r/month/2024/7/1` form rather than JS's 0-based months.
 */

export const CALENDAR_VIEWS: ViewKind[] = ['day', '2day', '4day', 'week', 'month', 'year'];

export function isViewKind(value: string | undefined | null): value is ViewKind {
  return !!value && (CALENDAR_VIEWS as string[]).includes(value);
}

/** Build the canonical URL for a given view + anchor date. */
export function buildCalendarPath(view: ViewKind, date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `/calendar/r/${view}/${year}/${month}/${day}`;
}

export type CalendarRouteParams = {
  view?: string;
  year?: string;
  month?: string;
  day?: string;
};

/**
 * Parse + validate URL params into a concrete view and date.
 * Returns null when anything is malformed so callers can redirect to a
 * canonical "today" URL instead of rendering garbage.
 */
export function parseCalendarParams(params: CalendarRouteParams): { view: ViewKind; date: Date } | null {
  const { view, year, month, day } = params;
  if (!isViewKind(view)) return null;

  const y = Number(year);
  const m = Number(month);
  const d = day === undefined ? 1 : Number(day);

  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (y < 1970 || y > 9999) return null;
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;

  const date = new Date(y, m - 1, d);
  // Reject overflow dates (e.g. Feb 31 → Mar 3).
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;

  return { view, date };
}
